import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Loader2, LifeBuoy, MessageSquare, CheckCircle2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";

const Stat = ({ label, value, icon: Icon }) => (
    <div className="rounded-2xl bg-card border p-5">
        <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><Icon className="w-5 h-5" /></div>
            <div>
                <div className="text-xs text-muted-foreground uppercase tracking-widest">{label}</div>
                <div className="font-display text-2xl font-bold">{value}</div>
            </div>
        </div>
    </div>
);

const SupportDashboard = () => {
    const [summary, setSummary] = useState(null);
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const [s, t] = await Promise.all([
                api.get("/team/support/summary"),
                api.get("/team/tickets"),
            ]);
            setSummary(s.data);
            setTickets(t.data.tickets || []);
        } catch {}
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const setStatus = async (t, status) => {
        try { await api.patch(`/team/tickets/${t.id}`, { status }); toast.success("Updated"); load(); }
        catch { toast.error("Failed"); }
    };

    if (loading) return <div className="p-10"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

    return (
        <div className="p-6 md:p-8" data-testid="team-support">
            <div className="text-xs uppercase tracking-widest text-brand-gold font-semibold">Support</div>
            <h1 className="font-display text-3xl font-bold tracking-tight">Customer Support</h1>

            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Open" value={summary?.open || 0} icon={LifeBuoy} />
                <Stat label="In progress" value={summary?.in_progress || 0} icon={Clock} />
                <Stat label="Resolved" value={summary?.resolved || 0} icon={CheckCircle2} />
                <Stat label="My tasks" value={summary?.my_tasks || 0} icon={MessageSquare} />
            </div>

            <div className="mt-8 space-y-3">
                {tickets.map((t) => (
                    <div key={t.id} className="rounded-2xl bg-card border p-5" data-testid={`ticket-${t.id}`}>
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <h3 className="font-semibold">{t.subject}</h3>
                                    <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${
                                        t.status === "resolved" ? "bg-emerald-100 text-emerald-700" :
                                        t.status === "in_progress" ? "bg-brand-lavender text-primary" :
                                        "bg-yellow-100 text-yellow-700"
                                    }`}>{t.status}</span>
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">
                                    From <strong>{t.name}</strong> · {t.email} · {new Date(t.created_at).toLocaleString()}
                                </div>
                            </div>
                            <div className="flex gap-2">
                                {t.status !== "in_progress" && <Button size="sm" onClick={() => setStatus(t, "in_progress")} variant="outline" className="rounded-full h-8">Start</Button>}
                                {t.status !== "resolved" && <Button size="sm" onClick={() => setStatus(t, "resolved")} className="rounded-full h-8 bg-emerald-600 hover:bg-emerald-700 text-white">Resolve</Button>}
                                <a href={`mailto:${t.email}?subject=Re: ${encodeURIComponent(t.subject)}`} className="text-xs px-3 py-1.5 rounded-full border">Reply via email</a>
                            </div>
                        </div>
                        <p className="text-sm leading-relaxed mt-3 whitespace-pre-line">{t.message}</p>
                    </div>
                ))}
                {tickets.length === 0 && (
                    <div className="rounded-2xl bg-card border p-14 text-center text-sm text-muted-foreground">
                        <LifeBuoy className="w-8 h-8 mx-auto opacity-40 mb-2" /> No tickets yet.
                    </div>
                )}
            </div>
        </div>
    );
};

export default SupportDashboard;
